import React, { useState, useEffect, useRef } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyles";
import logo from "../../logo.png";
import { SiteName } from "../../global/SiteName";
import { IoTrashBin, IoCopyOutline, IoCheckmark } from "react-icons/io5";
import SmallLoader from "../loaders/SmallLoader";
import toast from "react-hot-toast";
import api from "../../global/api";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const tabs = [
  "Package details",
  "Other details",
  "Shipping history",
  "Map view",
];

const formatStatus = (status) => {
  switch (status) {
    case "pending":
      return "Pending";
    case "in_transit":
      return "In transit";
    case "delivered":
      return "Delivered";
    default:
      return status;
  }
};

const ShippingDetailsCard = ({
  shippingPackage,
  admin,
  getShippingPackage,
}) => {
  const [currentTab, setCurrentTab] = useState("Package details");
  const [selectedId, setSelectedId] = useState(null);
  const [copied, setCopied] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const markersRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentTab === "Map view") {
      initializeMap();
    }

    return () => {
      cleanupMap();
    };
    // eslint-disable-next-line
  }, [currentTab, shippingPackage]);

  const copyTrackingId = () => {
    navigator.clipboard.writeText(shippingPackage.tracking_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const initializeMap = () => {
    if (!mapContainerRef.current || mapContainerRef.current._leaflet_map)
      return;

    const hasCoords =
      shippingPackage.origin_lat &&
      shippingPackage.origin_lng &&
      shippingPackage.destination_lat &&
      shippingPackage.destination_lng;

    const map = L.map(mapContainerRef.current).setView(
      hasCoords
        ? [
            (parseFloat(shippingPackage.origin_lat) +
              parseFloat(shippingPackage.destination_lat)) /
              2,
            (parseFloat(shippingPackage.origin_lng) +
              parseFloat(shippingPackage.destination_lng)) /
              2,
          ]
        : [0, 0],
      hasCoords ? 12 : 2
    );

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    // Clear any existing markers
    markersRef.current.forEach((marker) => {
      if (marker && map.hasLayer(marker)) {
        map.removeLayer(marker);
      }
    });
    markersRef.current = [];

    // Add origin marker if coordinates exist
    if (shippingPackage.origin_lat && shippingPackage.origin_lng) {
      const originMarker = L.marker(
        [
          parseFloat(shippingPackage.origin_lat),
          parseFloat(shippingPackage.origin_lng),
        ],
        {
          icon: L.icon({
            iconUrl: require("leaflet/dist/images/marker-icon.png"),
            iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
            shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
          }),
        }
      )
        .addTo(map)
        .bindPopup(
          `<b>Origin</b><br>${shippingPackage.origin || "Unknown location"}`
        )
        .openPopup();
      markersRef.current.push(originMarker);
    }

    // Add destination marker if coordinates exist
    if (shippingPackage.destination_lat && shippingPackage.destination_lng) {
      const destMarker = L.marker(
        [
          parseFloat(shippingPackage.destination_lat),
          parseFloat(shippingPackage.destination_lng),
        ],
        {
          icon: L.icon({
            iconUrl: require("leaflet/dist/images/marker-icon.png"),
            iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
            shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41],
          }),
        }
      )
        .addTo(map)
        .bindPopup(
          `<b>Destination</b><br>${
            shippingPackage.destination || "Unknown location"
          }`
        );
      markersRef.current.push(destMarker);
    }

    // Add tracking update markers if they exist
    if (
      shippingPackage.tracking_updates &&
      shippingPackage.tracking_updates.length > 0
    ) {
      shippingPackage.tracking_updates.forEach((update, index) => {
        if (update.latitude && update.longitude) {
          const updateMarker = L.marker(
            [parseFloat(update.latitude), parseFloat(update.longitude)],
            {
              icon: L.icon({
                iconUrl: require("leaflet/dist/images/marker-icon.png"),
                iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
                shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41],
              }),
            }
          )
            .addTo(map)
            .bindPopup(
              `<b>${formatStatus(update.status)}</b><br>${
                update.location
              }<br>${new Date(update.created_at).toLocaleString()}`
            );
          markersRef.current.push(updateMarker);
        }
      });
    }

    // Add route line if both points exist
    if (shippingPackage.origin_lat && shippingPackage.destination_lat) {
      const route = L.polyline(
        [
          [
            parseFloat(shippingPackage.origin_lat),
            parseFloat(shippingPackage.origin_lng),
          ],
          [
            parseFloat(shippingPackage.destination_lat),
            parseFloat(shippingPackage.destination_lng),
          ],
        ],
        { color: "#dc2626", weight: 2 }
      ).addTo(map);
      markersRef.current.push(route);

      // Fit map to show all points with padding
      const bounds = [
        [
          parseFloat(shippingPackage.origin_lat),
          parseFloat(shippingPackage.origin_lng),
        ],
        [
          parseFloat(shippingPackage.destination_lat),
          parseFloat(shippingPackage.destination_lng),
        ],
      ];

      // Include tracking update points in bounds if they exist
      if (shippingPackage.tracking_updates) {
        shippingPackage.tracking_updates.forEach((update) => {
          if (update.latitude && update.longitude) {
            bounds.push([
              parseFloat(update.latitude),
              parseFloat(update.longitude),
            ]);
          }
        });
      }

      map.fitBounds(bounds, { padding: [50, 50] });
    }

    mapRef.current = map;
  };

  const cleanupMap = () => {
    if (mapRef.current) {
      mapRef.current.off();
      mapRef.current.remove();
      mapRef.current = null;
    }
    markersRef.current = [];
  };

  const handleHistoryDelete = async (id) => {
    setSelectedId(id);
    try {
      const response = await api.post(`/tracking-update/${id}/destory`);
      toast.success(
        response.data.message || "History item deleted successfully"
      );
      if (getShippingPackage) {
        getShippingPackage();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to delete history item"
      );
    } finally {
      setSelectedId(null);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    // printWindow.document.write(`
    //   <!DOCTYPE html>
    //   <html>
    //     <head>
    //       <title>Invoice - ${shippingPackage.tracking_id}</title>
    //       <style>
    //         @page { size: A4; margin: 10mm; }
    //         body { font-family: Arial, sans-serif; color: #000; }
    //         .invoice-container { max-width: 800px; margin: 0 auto; padding: 20px; }
    //         .header { display: flex; justify-content: space-between; margin-bottom: 20px; border-bottom: 2px solid #dc2626; padding-bottom: 10px; }
    //         .company-info { display: flex; align-items: center; }
    //         .logo { height: 60px; margin-right: 15px; }
    //         .invoice-title { text-align: right; }
    //         .section { margin-bottom: 30px; }
    //         .flex-section { display: flex; justify-content: space-between; margin-bottom: 20px; }
    //         table { width: 100%; border-collapse: collapse; margin-top: 15px; }
    //         th { text-align: left; background-color: rgba(220, 38, 38, 0.1); padding: 8px; }
    //         td { padding: 8px; border-bottom: 1px solid rgba(220, 38, 38, 0.2); }
    //         .text-primary { color: #dc2626; }
    //         .text-secondary { color: #9ca3af; }
    //         .footer { margin-top: 40px; padding-top: 10px; border-top: 2px solid #dc2626; }
    //       </style>
    //     </head>
    //     <body>
    //       <div class="invoice-container">
    //         <div class="header">
    //           <div class="company-info">
    //             <img src="${logo}" alt="Company Logo" class="logo" />
    //             <div>
    //               <h1 class="text-primary">${SiteName}</h1>
    //               <p class="text-secondary">Shipping & Logistics</p>
    //             </div>
    //           </div>
    //           <div class="invoice-title">
    //             <h2 class="text-primary">INVOICE</h2>
    //             <p class="text-secondary">#${shippingPackage.tracking_id}</p>
    //           </div>
    //         </div>

    //         <div class="flex-section">
    //           <div style="width: 48%">
    //             <h3 class="text-primary">From:</h3>
    //             <p><strong>${shippingPackage.sender_name}</strong></p>
    //             <p>${shippingPackage.sender_email}</p>
    //             <p>${shippingPackage.sender_contact}</p>
    //             <p style="margin-top: 10px">${shippingPackage.origin}</p>
    //           </div>
    //           <div style="width: 48%; text-align: right">
    //             <h3 class="text-primary">To:</h3>
    //             <p><strong>${shippingPackage.receiver_name}</strong></p>
    //             <p>${shippingPackage.receiver_email}</p>
    //             <p>${shippingPackage.receiver_contact}</p>
    //             <p style="margin-top: 10px">${shippingPackage.destination}</p>
    //           </div>
    //         </div>

    //         <div class="section">
    //           <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; margin-bottom: 15px">
    //             <div>
    //               <h4 class="text-primary">Invoice Date</h4>
    //               <p>${new Date(
    //                 shippingPackage.created_at
    //               ).toLocaleDateString()}</p>
    //             </div>
    //             <div>
    //               <h4 class="text-primary">Delivery Status</h4>
    //               <p>${formatStatus(shippingPackage.status)}</p>
    //             </div>
    //             <div>
    //               <h4 class="text-primary">Delivery Duration</h4>
    //               <p>${shippingPackage.delivery_duration} day(s)</p>
    //             </div>
    //           </div>
    //         </div>

    //         <div class="section">
    //           <h3 class="text-primary">Package Details</h3>
    //           <table>
    //             <thead>
    //               <tr>
    //                 <th>Description</th>
    //                 <th style="text-align: right">Weight (lbs)</th>
    //                 <th style="text-align: right">Price</th>
    //               </tr>
    //             </thead>
    //             <tbody>
    //               <tr>
    //                 <td>${shippingPackage.description}</td>
    //                 <td style="text-align: right">${shippingPackage.weight}</td>
    //                 <td style="text-align: right">$${shippingPackage.price}</td>
    //               </tr>
    //               <tr>
    //                 <td><strong>Total</strong></td>
    //                 <td style="text-align: right">${
    //                   shippingPackage.weight
    //                 } lbs</td>
    //                 <td style="text-align: right"><strong>$${
    //                   shippingPackage.price
    //                 }</strong></td>
    //               </tr>
    //             </tbody>
    //           </table>
    //         </div>

    //         <div class="section">
    //           <h3 class="text-primary">Shipping History</h3>
    //           <div style="margin-top: 15px">
    //             ${shippingPackage.tracking_updates
    //               .slice()
    //               .reverse()
    //               .map(
    //                 (item) => `
    //                 <div style="border-left: 4px solid #dc2626; padding-left: 10px; margin-bottom: 10px">
    //                   <div style="display: flex; justify-content: space-between">
    //                     <div>
    //                       <p><strong class="text-primary">${
    //                         item.location
    //                       }</strong></p>
    //                       <p style="color: #9ca3af; font-size: 0.9em">
    //                         ${new Date(item.created_at).toLocaleString()}
    //                       </p>
    //                     </div>
    //                     <p style="text-transform: capitalize">${formatStatus(
    //                       item.status
    //                     )}</p>
    //                   </div>
    //                 </div>
    //               `
    //               )
    //               .join("")}
    //           </div>
    //         </div>

    //         <div class="footer">
    //           <div style="display: flex; justify-content: space-between">
    //             <div>
    //               <p style="color: #9ca3af">Thank you for choosing ${SiteName}</p>
    //               <p style="color: #9ca3af">For any inquiries, please contact our support team</p>
    //             </div>
    //             <div style="text-align: right">
    //               <p style="color: #9ca3af">Generated on: ${new Date().toLocaleString()}</p>
    //             </div>
    //           </div>
    //         </div>
    //       </div>
    //       <script>
    //         window.onload = function() {
    //           setTimeout(function() {
    //             window.print();
    //             window.close();
    //           }, 200);
    //         };
    //       </script>
    //     </body>
    //   </html>
    // `);
    printWindow.document.close();
  };

  const getCurrentLocation = () => {
    if (
      !shippingPackage.tracking_updates ||
      shippingPackage.tracking_updates.length === 0
    ) {
      return shippingPackage.origin;
    }
    return shippingPackage.tracking_updates[
      shippingPackage.tracking_updates.length - 1
    ].location;
  };

  return (
    <div className={GlobalStyles.sectionWrap}>
      <div className={`flex flex-col gap-3`}>
        <div className="w-full flex justify-center items-center gap-1">
          <img src={logo} alt="" className={GlobalStyles.formLogo} />
          <span className={GlobalStyles.navbarLogoText}>{SiteName}</span>
        </div>
        <div className="w-full flex justify-center">
          <div
            className={`flex items-center p-2 ${CommonClasses.rounded} bg-primary/10 gap-1`}
          >
            {tabs.map((item) => (
              <button
                key={item}
                className={`w-24 max-sm:w-20 text-xss max-sm:text-xsss py-1 capitalize ${
                  CommonClasses.transition
                } ${CommonClasses.rounded} ${
                  currentTab === item
                    ? "bg-primary text-light dark:text-dark"
                    : "text-primary"
                }`}
                onClick={() => setCurrentTab(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={copyTrackingId}
          className="flex items-center justify-center gap-2"
        >
          <p className="text-secondary">{shippingPackage.tracking_id}</p>
          <div
            className="text-primary hover:text-primary-dark transition-colors"
            title="Copy tracking ID"
          >
            {copied ? <IoCheckmark /> : <IoCopyOutline />}
          </div>
        </button>
        <div className="w-full flex justify-center">
          <p
            className={`${CommonClasses.rounded} ${CommonClasses.transition} capitalize text-xss bg-primary py-1 px-3 text-light dark:text-dark`}
          >
            Delivery status: {formatStatus(shippingPackage.status)}
          </p>
        </div>
        {currentTab === "Package details" && (
          <div className="w-full flex flex-col">
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Origin:</span>
              <span className={GlobalStyles.textOrder}>
                {shippingPackage.origin}
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Destination:</span>
              <span className={GlobalStyles.textOrder}>
                {shippingPackage.destination}
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>
                Shipment created on:
              </span>
              <span className={GlobalStyles.textOrder}>
                {new Date(shippingPackage.created_at).toLocaleString()}
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Current location:</span>
              <span className={GlobalStyles.textOrder}>
                {getCurrentLocation()}
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Delivery duration:</span>
              <span className={GlobalStyles.textOrder}>
                {shippingPackage.delivery_duration} day(s)
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Weight(lbs):</span>
              <span className={GlobalStyles.textOrder}>
                {shippingPackage.weight}
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Shipping fee:</span>
              <span className={GlobalStyles.textOrder}>
                $
                {Number(shippingPackage.price).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        )}
        {currentTab === "Other details" && (
          <div className="w-full flex flex-col">
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Reciever's name:</span>
              <span className={GlobalStyles.textOrder}>
                {shippingPackage.receiver_name}
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Reciever's email:</span>
              <span className={GlobalStyles.textOrder}>
                {shippingPackage.receiver_email}
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>
                Reciever's contact:
              </span>
              <span className={GlobalStyles.textOrder}>
                {shippingPackage.receiver_contact}
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Sender's name:</span>
              <span className={GlobalStyles.textOrder}>
                {shippingPackage.sender_name}
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Sender's email:</span>
              <span className={GlobalStyles.textOrder}>
                {shippingPackage.sender_email}
              </span>
            </div>
            <div className={GlobalStyles.seperator2}>
              <span className={GlobalStyles.textOrder}>Sender's contact:</span>
              <span className={GlobalStyles.textOrder}>
                {shippingPackage.sender_contact}
              </span>
            </div>
          </div>
        )}
        {currentTab === "Shipping history" && (
          <div className="w-full flex flex-col gap-1 border-primary h-56 overflow-scroll">
            {shippingPackage.tracking_updates
              .slice()
              .reverse()
              .map((item, index, array) => (
                <div key={item.id} className="flex bg-primary/10 items-center">
                  <div className="w-full p-5 flex flex-col">
                    <span className="text-primary text-sm">
                      {index === array.length - 1 ? (
                        <span>Previously at {item.location}</span>
                      ) : (
                        <span className="font-semibold">
                          Currently at {item.location}
                        </span>
                      )}
                    </span>
                    <span className="text-secondary text-xss">
                      On {new Date(item.created_at).toLocaleString()}
                    </span>
                    <span className="text-secondary text-xss capitalize">
                      {formatStatus(item.status)}
                    </span>
                    {item.notes && (
                      <span className="text-primary text-xss capitalize">
                        Important: {item.notes}
                      </span>
                    )}
                  </div>
                  {admin && (
                    <button
                      onClick={() => handleHistoryDelete(item.id)}
                      className="w-10 h-10 text-primary flex justify-center items-center"
                    >
                      {selectedId !== item.id ? (
                        <IoTrashBin />
                      ) : (
                        <SmallLoader />
                      )}
                    </button>
                  )}
                </div>
              ))}
          </div>
        )}
        {currentTab === "Map view" && (
          <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 relative">
            <div
              ref={mapContainerRef}
              className="w-full h-full"
              style={{ minHeight: "384px" }}
            ></div>
            {(!shippingPackage.origin_lat ||
              !shippingPackage.destination_lat) && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                <div className="text-center p-4 bg-white bg-opacity-80 rounded-lg">
                  <p>Location data not available for this shipment</p>
                  <p className="text-sm">
                    Please check if origin and destination coordinates were
                    saved
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        <div
          className={`${CommonClasses.rounded} py-2 px-4 bg-primary/10 text-primary text-xs`}
        >
          Description: {shippingPackage.description}
        </div>
        <div className={GlobalStyles.seperator}>
          <button
            className={GlobalStyles.buttonFullAlt}
            onClick={() => navigate(-1)}
          >
            Back
          </button>
          <button className={GlobalStyles.buttonFull} onClick={handlePrint}>
            Print Invoice
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShippingDetailsCard;
